import React from "react";
import { CheckCircle2, Circle, Dot } from "lucide-react";

/** Portal-only status chip. Semantic dot + text label. */
export const PortalStatusChip = ({ tone = "neutral", children, testid }) => {
  const tones = {
    neutral: { dot: "var(--fg-muted)", fg: "var(--fg-secondary)", bg: "var(--bg-inset)" },
    lime:    { dot: "#B7E31D", fg: "var(--fg-primary)", bg: "rgba(183,227,29,0.10)" },
    hold:    { dot: "#D79A27", fg: "var(--fg-primary)", bg: "rgba(215,154,39,0.10)" },
    blue:    { dot: "#1B4F8A", fg: "var(--fg-primary)", bg: "rgba(27,79,138,0.08)" },
    critical:{ dot: "#B83C3C", fg: "var(--fg-primary)", bg: "rgba(184,60,60,0.08)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium"
      style={{ background: t.bg, color: t.fg }}
      data-testid={testid}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
      {children}
    </span>
  );
};

/** Airy section shell — no boxy card unless emphasized. */
export const PortalPageShell = ({ eyebrow, title, subtitle, right, children, testid }) => (
  <div data-testid={testid} className="w-full">
    <div className="mb-6 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold"
               style={{ color: "var(--fg-muted)" }}>
            {eyebrow}
          </div>
        )}
        {title && (
          <h1 className="mt-1 text-[24px] md:text-[28px] font-semibold leading-tight"
              style={{ color: "var(--fg-primary)", fontFamily: "'Montserrat', 'Inter', sans-serif" }}>
            {title}
          </h1>
        )}
        {subtitle && (
          <div className="mt-1 text-[15px] md:text-[18px]" style={{ color: "var(--fg-secondary)" }}>
            {subtitle}
          </div>
        )}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
    {children}
  </div>
);

/** Section separator — thin hairline, generous vertical space. */
export const PortalSection = ({ label, children, right }) => (
  <section className="py-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
    <div className="flex items-center justify-between mb-4">
      {label && (
        <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold"
             style={{ color: "var(--fg-muted)" }}>
          {label}
        </div>
      )}
      {right}
    </div>
    {children}
  </section>
);

/** Action Required panel — high visibility card with lime left rule when active. */
export const PortalActionCard = ({ tone = "lime", eyebrow, title, description, action, testid }) => {
  const accent = tone === "lime" ? "#B7E31D" : tone === "hold" ? "#D79A27" : "var(--border-strong)";
  return (
    <div
      className="rounded-md p-5 md:p-6 flex items-start gap-4"
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-default)",
        borderLeft: `3px solid ${accent}`,
      }}
      data-testid={testid}
    >
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold mb-1"
               style={{ color: accent === "var(--border-strong)" ? "var(--fg-muted)" : accent }}>
            {eyebrow}
          </div>
        )}
        {title && (
          <div className="text-[16px] md:text-[17px] font-semibold" style={{ color: "var(--fg-primary)" }}>
            {title}
          </div>
        )}
        {description && (
          <div className="text-[13.5px] mt-1" style={{ color: "var(--fg-secondary)" }}>
            {description}
          </div>
        )}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
};

/** Small overview card — link-like row that routes to a sub-page. */
export const PortalOverviewCard = ({ label, primary, meta, onClick, testid }) => (
  <button
    onClick={onClick}
    className="w-full text-left rounded-md p-4 transition-colors group"
    style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border-default)",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-default)"; }}
    data-testid={testid}
  >
    <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold" style={{ color: "var(--fg-muted)" }}>
      {label}
    </div>
    <div className="mt-1 text-[15px] font-semibold flex items-center gap-2" style={{ color: "var(--fg-primary)" }}>
      <span className="truncate">{primary || "—"}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" className="opacity-60 group-hover:opacity-100 transition-opacity">
        <path d="M9 18l6-6-6-6" />
      </svg>
    </div>
    {meta && <div className="mt-1 text-[12.5px]" style={{ color: "var(--fg-secondary)" }}>{meta}</div>}
  </button>
);

/** Tour lifecycle stepper — horizontal on desktop, vertical on mobile. */
export const TourLifecycleStepper = ({ steps, testid = "portal-lifecycle" }) => {
  return (
    <>
      {/* Desktop / tablet horizontal */}
      <ol className="hidden sm:flex items-start w-full gap-2" data-testid={testid}>
        {steps.map((s, i) => {
          const isLast = i === steps.length - 1;
          const dotColor =
            s.state === "complete" ? "#B7E31D"
              : s.state === "current" ? "#D79A27"
                : "var(--border-strong)";
          const isCurrent = s.state === "current";
          return (
            <li key={s.key} className="flex-1 min-w-0" data-testid={`lifecycle-${s.key}`}>
              <div className="flex items-center">
                <span className="relative inline-flex items-center justify-center w-4 h-4 shrink-0">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{
                      background: s.state === "future" ? "transparent" : dotColor,
                      border: `2px solid ${dotColor}`,
                    }}
                  />
                  {isCurrent && (
                    <span
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ background: "#D79A27", opacity: 0.35 }}
                    />
                  )}
                </span>
                {!isLast && (
                  <span
                    className="flex-1 h-px mx-2"
                    style={{
                      background:
                        s.state === "complete" ? "#B7E31D" : "var(--border-default)",
                    }}
                  />
                )}
              </div>
              <div className="mt-2 pr-2">
                <div
                  className="text-[11.5px] uppercase tracking-[0.12em] font-semibold"
                  style={{
                    color: s.state === "future" ? "var(--fg-muted)" : "var(--fg-primary)",
                  }}
                >
                  {s.label}
                </div>
                {s.meta && (
                  <div className="text-[11.5px] mt-0.5" style={{ color: "var(--fg-secondary)" }}>
                    {s.meta}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Mobile vertical */}
      <ol className="sm:hidden space-y-4" data-testid={`${testid}-mobile`}>
        {steps.map((s, i) => {
          const dotColor =
            s.state === "complete" ? "#B7E31D"
              : s.state === "current" ? "#D79A27"
                : "var(--border-strong)";
          return (
            <li key={s.key} className="flex items-start gap-3">
              <span className="relative inline-flex items-center justify-center w-4 h-4 mt-0.5 shrink-0">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: s.state === "future" ? "transparent" : dotColor,
                    border: `2px solid ${dotColor}`,
                  }}
                />
              </span>
              <div>
                <div
                  className="text-[12px] uppercase tracking-[0.12em] font-semibold"
                  style={{ color: s.state === "future" ? "var(--fg-muted)" : "var(--fg-primary)" }}
                >
                  {s.label}
                </div>
                {s.meta && (
                  <div className="text-[12.5px] mt-0.5" style={{ color: "var(--fg-secondary)" }}>
                    {s.meta}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
};

/** Vertical routing timeline — used after routing submission. */
export const RoutingTimeline = ({ stops = [], testid = "routing-timeline" }) => {
  if (stops.length === 0) {
    return (
      <div className="text-[13px]" style={{ color: "var(--fg-secondary)" }}>
        No stops parsed yet.
      </div>
    );
  }
  return (
    <ol className="relative" data-testid={testid}>
      {stops.map((s, i) => {
        const isLast = i === stops.length - 1;
        return (
          <li key={i} className="relative pl-8 pb-8" data-testid={`routing-stop-${i}`}>
            {/* Dot */}
            <span
              className="absolute left-2 top-1.5 w-2.5 h-2.5 rounded-full"
              style={{ background: "#B7E31D", boxShadow: "0 0 0 3px var(--bg-surface)" }}
            />
            {/* Connector */}
            {!isLast && (
              <span
                className="absolute left-[13px] top-4 bottom-0 w-px"
                style={{ background: "var(--border-default)" }}
              />
            )}
            <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold"
                 style={{ color: "var(--fg-muted)" }}>
              {s.date || "—"}
            </div>
            <div className="text-[16px] md:text-[17px] font-semibold mt-0.5"
                 style={{ color: "var(--fg-primary)" }}>
              {s.city || "Stop"}{s.state ? `, ${s.state}` : ""}
            </div>
            {s.venue && (
              <div className="text-[13.5px] mt-0.5" style={{ color: "var(--fg-secondary)" }}>
                {s.venue}
              </div>
            )}
            {!isLast && s.leg && (
              <div className="mt-3 text-[11.5px] uppercase tracking-[0.10em] font-medium"
                   style={{ color: "var(--fg-muted)" }}>
                {s.leg}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

/** Primary button — coach-blue solid. */
export const PortalPrimaryBtn = ({ children, className = "", ...rest }) => (
  <button
    {...rest}
    className={`inline-flex items-center justify-center h-11 px-5 rounded-md text-[13.5px] font-semibold transition-colors ${className}`}
    style={{
      background: "#1B4F8A",
      color: "#FFFFFF",
      minHeight: 44,
      minWidth: 160,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "#154070"; rest.onMouseEnter?.(e); }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "#1B4F8A"; rest.onMouseLeave?.(e); }}
  >
    {children}
  </button>
);

/** Ghost button — text link with border on hover. */
export const PortalGhostBtn = ({ children, className = "", tone = "secondary", ...rest }) => {
  const fg = tone === "critical" ? "#B83C3C" : "var(--fg-secondary)";
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center h-11 px-4 rounded-md text-[13.5px] font-medium transition-colors ${className}`}
      style={{ color: fg, background: "transparent", border: "1px solid transparent", minHeight: 44 }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--border-default)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
    >
      {children}
    </button>
  );
};

export const PortalOutlineBtn = ({ children, className = "", ...rest }) => (
  <button
    {...rest}
    className={`inline-flex items-center justify-center h-11 px-5 rounded-md text-[13.5px] font-semibold transition-colors ${className}`}
    style={{
      background: "transparent",
      color: "var(--fg-primary)",
      border: "1px solid var(--border-strong)",
      minHeight: 44,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-inset)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
  >
    {children}
  </button>
);

export const StatusIcon = ({ state, size = 16 }) => {
  if (state === "complete") return <CheckCircle2 size={size} style={{ color: "#B7E31D" }} />;
  if (state === "current")  return <Dot size={size} style={{ color: "#D79A27" }} />;
  return <Circle size={size} style={{ color: "var(--border-strong)" }} />;
};

/** Map raw tour status → step states for the lifecycle stepper. */
export const deriveLifecycleSteps = ({ tour, quote, docs, routing }) => {
  const status = (tour?.status || "").toLowerCase();
  const hasQuote = !!quote;
  const quoteAccepted = quote?.status === "accepted";
  const quoteDeclined = quote?.status === "declined";
  const hasContract = (docs || []).some((d) => d.kind === "contract");
  const contractExecuted = (docs || []).some((d) => d.kind === "contract" && d.status === "executed");
  const isDeparted = ["in_progress", "completed", "closed", "departed", "active"].includes(status);

  const step = (key, label) => ({ key, label, state: "future" });

  const steps = [
    step("booked",   "Booked"),
    step("routing",  "Routing"),
    step("quote",    "Quote"),
    step("contract", "Contract"),
    step("departure","Departure"),
  ];

  // BOOKED — always complete if we can see the tour
  if (tour) steps[0].state = "complete";

  // ROUTING — server-authoritative. Complete when any authoritative
  // shared routing exists OR the tour has already progressed past inquiry.
  // Portal-only submitted-but-not-yet-shared is NOT "complete" — it stays
  // current with an "Under review" hint.
  const sharedCount = (routing && routing.shared) ? routing.shared.length : 0;
  const hasSubmitted = !!(routing && routing.submitted);
  if (sharedCount > 0) {
    steps[1].state = "complete";
  } else if (hasSubmitted) {
    steps[1].state = "current";
    steps[1].meta = "Under review by All Access";
  } else if (status && status !== "inquiry") {
    steps[1].state = "complete";
  }

  // QUOTE
  if (quoteAccepted || hasContract) {
    steps[2].state = "complete";
  } else if (hasQuote && !quoteDeclined) {
    steps[2].state = "current";
    steps[2].meta = "Awaiting your approval";
  } else if (quoteDeclined) {
    steps[2].state = "current";
    steps[2].meta = "Declined — awaiting revision";
  }

  // CONTRACT
  if (contractExecuted) {
    steps[3].state = "complete";
  } else if (hasContract) {
    steps[3].state = "current";
    steps[3].meta = "In progress";
  } else if (quoteAccepted) {
    steps[3].state = "current";
    steps[3].meta = "Awaiting contract";
  }

  // DEPARTURE
  if (isDeparted) {
    steps[4].state = "complete";
  } else if (contractExecuted) {
    steps[4].state = "current";
    steps[4].meta = "Scheduled";
  }

  return steps;
};

/** Format money helper */
export const fmtMoney = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Simple US date → e.g. Sep 12, 2026 */
export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return iso;
  }
};

/** Human status → chip tone/label */
export const humanTourStatus = (s) => {
  const v = (s || "").toLowerCase();
  if (["confirmed", "booked", "scheduled"].includes(v)) return { tone: "lime", label: "Confirmed" };
  if (["in_progress", "active", "departed"].includes(v)) return { tone: "blue", label: "In Progress" };
  if (["hold", "on_hold"].includes(v)) return { tone: "hold", label: "On Hold" };
  if (["completed", "closed"].includes(v)) return { tone: "neutral", label: "Completed" };
  if (["inquiry"].includes(v)) return { tone: "hold", label: "Inquiry" };
  return { tone: "neutral", label: (s || "Unknown").replaceAll("_", " ") };
};
